import { Component, ElementRef, OnDestroy, OnInit, AfterViewInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { Subject, fromEvent } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';

import { OperationalTask } from '../../api/models/operational-task';
import { OperationalTaskComment } from '../../api/models/operational-task-comment';
import { OperationalTasksService } from '../../api/services/operational-tasks.service';
import { PaginationDataSource } from '../../model/pagination-data-source';

// The status filter: a task status, or '' for all of them.
type TaskStatusFilter = OperationalTask['status'] | '';

// AI: Query model for the operational tasks list.
class TaskQuery {
  constructor(
    public status: TaskStatusFilter = 'OPEN',
    public category: string = '',
  ) {}
}

@Component({
  selector: 'app-operational-tasks-list-page',
  templateUrl: './operational-tasks-list-page.component.html',
  styleUrls: ['./operational-tasks-list-page.component.scss']
})
export class OperationalTasksListPageComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly ngDestroy$ = new Subject<void>();
  @ViewChild('table', { read: ElementRef }) public matTableRef!: ElementRef;

  public readonly columns = ['created_at', 'category', 'title', 'reference', 'context_info', 'status', 'actions'];
  public statusFilter: TaskStatusFilter = 'OPEN';
  public categoryFilter = '';

  // AI: Known categories for the filter dropdown.
  public readonly categories = [
    { value: '', label: 'All Categories' },
    { value: 'payment_error', label: 'Payment Errors' },
    // AI: Add new categories here as producers are added.
  ];

  public readonly dataSource = new PaginationDataSource<OperationalTask, TaskQuery>(
    (request, query) => this.tasksService.listOperationalTasks({
      status: query.status || undefined,
      category: query.category || undefined,
      after: request.after,
      limit: request.limit
    }),
    new TaskQuery('OPEN', ''),
    50,
  );

  constructor(
    private readonly tasksService: OperationalTasksService,
    private readonly router: Router,
    private readonly activatedRoute: ActivatedRoute,
  ) {}

  public ngOnInit(): void {
    this.activatedRoute.queryParamMap
      .pipe(takeUntil(this.ngDestroy$))
      .subscribe(params => {
        const status = params.get('status');
        this.statusFilter = status === 'OPEN' || status === 'RESOLVED' ? status : 'OPEN';
        this.categoryFilter = params.get('category') || '';
        this.dataSource.queryBy(new TaskQuery(this.statusFilter, this.categoryFilter));
      });
  }

  public ngAfterViewInit(): void {
    fromEvent<Event>(this.matTableRef.nativeElement, 'scroll')
      .pipe(debounceTime(700), takeUntil(this.ngDestroy$))
      .subscribe((e) => this.onTableScroll(e));
  }

  public ngOnDestroy(): void {
    this.ngDestroy$.next();
    this.ngDestroy$.complete();
  }

  public updateFilters(params: Partial<{ status: TaskStatusFilter; category: string }>): void {
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: params,
      queryParamsHandling: 'merge'
    });
  }

  // AI: Navigate to the referenced entity (e.g., invoice page for payment errors).
  public navigateToReference(task: OperationalTask): void {
    const context = task.context;
    if (task.reference_type === 'outgoing_payment' && context?.invoice_id) {
      this.router.navigate(['/invoices', context.invoice_id]);
    }
    // AI: Future: handle other reference types.
  }

  // AI: Retry payment: resolves the task and sends retry signal to Temporal.
  public retryPayment(task: OperationalTask, event: MouseEvent): void {
    event.stopPropagation();
    this.tasksService.retryPaymentFromTask({ id: task.id }).subscribe(() => {
      this.dataSource.queryBy(new TaskQuery(this.statusFilter, this.categoryFilter));
      if (this.expandedTaskId === task.id) {
        this.expandedTaskId = null;
      }
    });
  }

  // AI: Resolve without action (manual investigation completed).
  public resolveTask(task: OperationalTask, event: MouseEvent): void {
    event.stopPropagation();
    this.tasksService.resolveOperationalTask({
      id: task.id,
      body: { note: 'Manually resolved from console' }
    }).subscribe(() => {
      this.dataSource.queryBy(new TaskQuery(this.statusFilter, this.categoryFilter));
      if (this.expandedTaskId === task.id) {
        this.expandedTaskId = null;
      }
    });
  }

  // AI: Determines which action buttons to show based on category.
  public canRetryPayment(task: OperationalTask): boolean {
    return task.status === 'OPEN' && task.category === 'payment_error' && !!task.workflow_id;
  }

  // --- Comment support ---

  // AI: Track which task's comments are expanded.
  public expandedTaskId: number | null = null;
  public comments: OperationalTaskComment[] = [];
  public newCommentText = "";

  public toggleComments(task: OperationalTask, event: MouseEvent): void {
    event.stopPropagation();
    if (this.expandedTaskId === task.id) {
      this.expandedTaskId = null;
      this.comments = [];
      return;
    }
    this.expandedTaskId = task.id;
    this.loadComments(task.id);
  }

  private loadComments(taskId: number): void {
    this.tasksService
      .listOperationalTaskComments({ id: taskId })
      .subscribe((comments) => {
        this.comments = comments;
      });
  }

  public addComment(taskId: number): void {
    if (!this.newCommentText.trim()) {
      return;
    }
    this.tasksService
      .createOperationalTaskComment({
        id: taskId,
        body: { content: this.newCommentText.trim() },
      })
      .subscribe((comment) => {
        this.comments = [...this.comments, comment];
        this.newCommentText = "";
        // AI: We could update the comment count in the list here, but for simplicity
        // AI: we just keep the locally added comment in the detail view.
      });
  }

  private onTableScroll(e: Event): void {
    // The table is the scroll's target: a scroll event does not bubble.
    const table = e.target;
    if (!(table instanceof HTMLElement)) {
      return;
    }
    const tableViewHeight = table.offsetHeight;
    const tableScrollHeight = table.scrollHeight;
    const scrollLocation = table.scrollTop;
    const scrollDownLimit = tableScrollHeight - tableViewHeight - 200;
    if (scrollLocation > scrollDownLimit && scrollDownLimit > 0) {
      this.dataSource.fetchNextPage();
    }
  }
}
